'use client';
import React, { createContext, useContext, useCallback, useReducer } from 'react';
import { ToastContextValue, ToastProps, ToastOptions } from './types';

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

type ToastAction =
  | { type: 'ADD_TOAST'; toast: ToastProps }
  | { type: 'REMOVE_TOAST'; id: string }
  | { type: 'REMOVE_ALL' };

function toastReducer(state: ToastProps[], action: ToastAction): ToastProps[] {
  switch (action.type) {
    case 'ADD_TOAST':
      return [...state, action.toast];
    case 'REMOVE_TOAST':
      return state.filter(toast => toast.id !== action.id);
    case 'REMOVE_ALL':
      return [];
    default:
      return state;
  }
}

let toastCount = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, dispatch] = useReducer(toastReducer, []);

  const addToast = useCallback((toast: ToastOptions) => {
    const id = `toast-${toastCount++}`;
    dispatch({ type: 'ADD_TOAST', toast: { ...toast, id } });
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_TOAST', id });
  }, []);

  const removeAll = useCallback(() => {
    dispatch({ type: 'REMOVE_ALL' });
  }, []);

  const value = {
    toasts,
    addToast,
    removeToast,
    removeAll,
  };

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToastContext() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
}
